import { FlipHorizontal2, Trash2 } from "lucide-react";
import { flipBoard } from "@/lib/canvas/flip-board";
import Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { useEffect, useMemo, useRef, useState } from "react";
import { Layer, Stage, Transformer } from "react-konva";
import type {
  BoardScene,
  BoardShape,
  BoardToken,
} from "@/lib/canvas/board-schema";
import {
  clampShape,
  clampToken,
  emptyScene,
  MAX_SHAPES,
  MAX_TOKENS,
} from "@/lib/canvas/board-schema";
import { pointToPx, type Dims } from "@/lib/canvas/coords";
import { unitRectFromNodeBox } from "@/lib/canvas/shape-edit";
import {
  formationsForGameFormat,
  GAME_FORMATS,
  playerTokensFromFormation,
  type GameFormat,
} from "@/lib/canvas/formations";
import type { SharedShape } from "@/lib/canvas/shared-shape";
import {
  isToolAllowed,
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { ColorPalette } from "./components/ColorPalette";
import { PitchBackground } from "./components/PitchBackground";
import { ShapeRenderer } from "./components/ShapeRenderer";
import { StrokeWidthSlider } from "./components/StrokeWidthSlider";
import { DrawingStyle } from "./components/DrawingStyle";
import { ToolGrid } from "./components/ToolGrid";
import { TokenRenderer } from "./components/TokenRenderer";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CanvasRailControls } from "./components/CanvasRailControls";
import { EndpointHandles } from "./components/EndpointHandles";
import { InlineTextEditor } from "./components/InlineTextEditor";
import { useStageCursors } from "./useStageCursors";
import { useUndoStack } from "@/lib/canvas/useUndoStack";
import type { Player } from "@/lib/types";

/**
 * Full tactical-board editor.
 *
 * Drag-state machine, driven by Konva stage events and persisted in
 * the `review-editor` store's `tactical` surface:
 *
 *   1. mousedown on empty stage with a shape tool armed:
 *      - arrow / line: pending shape with x1=x2 at the pointer
 *      - zone: pending zone with w=h=tiny near the pointer
 *      - freehand: pending shape with `points: [click]`
 *      - label: read text from the anchored tool dropdown, place at the
 *        pointer, disarm
 *      - player / ball: place a token at the pointer; the tool
 *        STAYS armed so a lineup can be dropped in one go (Esc or
 *        another tool disarms). Player tokens take the selected
 *        swatch color.
 *      - select: clear selection (the void was clicked)
 *   2. mousemove during drag → update pending via the store
 *   3. mouseup → commit pending; reject degenerate shapes
 *
 * Selection + direct manipulation: per-node onClick wires through
 * `selectShape`, and every shape/token is draggable whenever the
 * editor is active (drag-start auto-selects) — EXCEPT while the
 * freehand pen is armed, so the pen can draw over existing shapes.
 * Zones get a Konva.Transformer attached so resize-handles work for
 * free (replaces the legacy hand-rolled 8-handle code at tactical-
 * board.js:524-541). Selected arrows/lines get draggable endpoint
 * anchors (`EndpointHandles`) so either end can be repositioned.
 * Labels and zone captions open an inline text editor on
 * double-click.
 *
 * Apply Formation: replaces existing player tokens after a confirm
 * dialog. Preserves the ball + every non-player shape. The "custom"
 * sentinel just tags the scene with formation metadata without
 * synthesizing positions.
 *
 * Keyboard shortcuts (legacy mapping): V/A/L/Z/T/F/P/B arm the
 * matching tool. Delete/Backspace removes the selected shape or
 * token. Cmd/Ctrl+Z undoes; Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y redoes.
 * Esc steps down: cancel an in-flight draw → clear selection →
 * disarm (back to select).
 */

const TACTICAL_TOOLS: readonly EditorToolKind[] = [
  "select",
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
  "player",
  "ball",
];

let _idCounter = 0;
function nextId(prefix: string): string {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter}`;
}

// Ignore sub-4px pointer jitter so a tap on a draggable node stays a
// click (select) instead of becoming a no-op drag that pollutes the
// undo stack. NOTE: `Konva.dragDistance` is app-global (Konva's
// default is 3); the canvas editors are the only draggable Konva
// surfaces, so the 3→4 bump has no side effects elsewhere.
Konva.dragDistance = 4;

export type TacticalBoardEditorProps = {
  scene: BoardScene;
  onSceneChange: (scene: BoardScene) => void;
  appearance?: "grass" | "navy";
  /** Stage width in pixels. The stage is 16:9 (height = width ×
   * 9/16) — a green field with the pitch markings drawn inset at
   * true proportions, so the board matches the video stage's
   * footprint with no letterbox bars. */
  width: number;
  /** When true, render only the pitch stage — no side toolbar.
   * Used by Coach Review focus mode, which renders an external
   * floating tool overlay that drives the same review-editor store.
   * The canvas's own side toolbar would conflict with the overlay. */
  hideToolbar?: boolean;
  /** Extra content appended to the right-rail toolbar, below the
   * built-in tools / color / stroke / formation / clear-board
   * controls. Coach Review uses this to stack the template
   * selector + composer in the same column as the tools so the
   * coach can type notes while watching the pitch. Ignored when
   * `hideToolbar` is true. */
  toolbarChildren?: React.ReactNode;
  /** Coach Review v2 — tool-rail relocation slot. When provided,
   * the tool grid / color palette / stroke slider render into the
   * caller's slot (the v2 right rail) instead of the canvas's own
   * column. The relocated block drives the same `review-editor`
   * store. Ignored when `hideToolbar` is true.
   * See `TelestratorCanvasProps.toolRailSlot` for the full
   * rationale. */
  toolRailSlot?: (toolControls: React.ReactNode) => React.ReactNode;
  /** Coach Review v2 — formation controls relocation slot. When
   * provided, the game-format / formation picker + Apply Formation
   * action render into the caller's top control row instead of the
   * tools rail. Ignored when `hideToolbar` is true. */
  formationControlsSlot?: (
    formationControls: React.ReactNode,
  ) => React.ReactNode;
  /** Focus mode follows the approved compact footer: the active game
   * format still determines the available formations, but its selector
   * remains in the regular Review context row. */
  compactFormationControls?: boolean;
  /** Authorized roster already carried by the Coach Review bundle.
   * Player-token settings use it in the anchored Player dropdown. */
  players?: Player[];
};

export function TacticalBoardEditor(props: TacticalBoardEditorProps) {
  // The board stage is a 16:9 GREEN field — the pitch markings are
  // drawn inset at true proportions inside it (see `PitchBackground`),
  // so the surface matches the video stage's 16:9 footprint without
  // black letterbox bars. The whole green area is the working
  // surface; `dims` is the full 16:9 stage and 0..1 coords map to
  // it, so a token can sit anywhere on the green.
  const width = Math.max(1, Math.round(props.width));
  const height = Math.max(1, Math.round(width * (9 / 16)));
  const dims: Dims = useMemo(() => ({ width, height }), [width, height]);

  const armedTool = useReviewEditorStore((s) => s.tactical.armedTool);
  const color = useReviewEditorStore((s) => s.tactical.color);
  const strokeWidth = useReviewEditorStore((s) => s.tactical.strokeWidth);
  const selectedId = useReviewEditorStore((s) => s.tactical.selectedShapeId);
  const pending = useReviewEditorStore((s) => s.tactical.pendingShape);
  const armTool = useReviewEditorStore((s) => s.armTool);
  const setColor = useReviewEditorStore((s) => s.setColor);
  const setStrokeWidth = useReviewEditorStore((s) => s.setStrokeWidth);
  const setSurface = useReviewEditorStore((s) => s.setSurface);
  const selectShape = useReviewEditorStore((s) => s.selectShape);
  const startPending = useReviewEditorStore((s) => s.startPending);
  const updatePending = useReviewEditorStore((s) => s.updatePending);
  const commitPending = useReviewEditorStore((s) => s.commitPending);
  const cancelPending = useReviewEditorStore((s) => s.cancelPending);
  const disarm = useReviewEditorStore((s) => s.disarm);

  // Make sure the store is pinned to the tactical surface whenever
  // this component renders (the demo route swaps surfaces).
  useEffect(() => {
    setSurface("tactical");
  }, [setSurface]);

  // ---- Local formation-picker state ----
  const [gameFormat, setGameFormat] = useState<GameFormat>(
    props.scene.game_format ?? "11v11",
  );
  const [formationId, setFormationId] = useState<string>(
    props.scene.formation ?? formationsForGameFormat(gameFormat)[0]?.id ?? "",
  );

  // Label text lives in an anchored tool dropdown, consistent with the
  // drawing-style control and without interrupting the canvas with a modal.
  const [labelText, setLabelText] = useState("");
  const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);
  const [playerPopoverOpen, setPlayerPopoverOpen] = useState(false);
  const previousArmedTool = useRef(armedTool);
  useEffect(() => {
    if (previousArmedTool.current !== armedTool) {
      setLabelPopoverOpen(armedTool === "label");
      if (armedTool !== "player") setPlayerPopoverOpen(false);
      previousArmedTool.current = armedTool;
    }
  }, [armedTool]);

  // Direct-manipulation pass: id of the label/zone whose text is
  // being edited via the double-click inline editor.
  const [editingTextId, setEditingTextId] = useState<string | null>(null);

  // M7.5 B4 / B6: styled confirms for clear-board + Apply
  // Formation, replacing `window.confirm`.
  const [confirm, setConfirm] = useState<
    | { kind: "clear-board" }
    | { kind: "apply-formation"; pendingFormationId: string }
    | null
  >(null);

  // M7.5 C1 / C2: Canvas-rail state — visibility toggle + the
  // multi-step undo stack (cap 10, clears on remount).
  const [canvasVisible, setCanvasVisible] = useState(true);
  const undoStack = useUndoStack<BoardScene>({ maxDepth: 10 });

  // Direct manipulation: every shape/token is draggable while the
  // editor is active — except under the freehand pen, which must be
  // able to draw over existing shapes.
  const shapesDraggable = armedTool !== "freehand";
  const cursors = useStageCursors({ armedTool });

  const selectedPlayerToken =
    selectedId == null
      ? undefined
      : props.scene.tokens.find(
          (token) => token.id === selectedId && token.kind === "player",
        );
  useEffect(() => {
    if (!selectedPlayerToken) {
      setPlayerPopoverOpen(false);
    } else if (armedTool === "player") {
      // Placement commits through the parent scene. During that render handoff
      // the selected id exists one frame before the token does; reopening here
      // keeps the roster picker anchored beneath Player after a new token lands.
      setPlayerPopoverOpen(true);
    }
  }, [armedTool, selectedPlayerToken?.id]);

  // The freshest scene, independent of render timing. Two consumers:
  //   - the window keydown handler (its effect re-registers via a
  //     PASSIVE effect, so a rapid key-repeat can hit a stale closure
  //     between React's commit and the listener swap — undo/redo must
  //     never snapshot a stale scene);
  //   - same-task double commits (e.g. two touch drags lifting in one
  //     touchend fire both dragends synchronously — the second must
  //     see the first's write, not the render-time props).
  const sceneRef = useRef(props.scene);
  sceneRef.current = props.scene;
  const onSceneChangeRef = useRef(props.onSceneChange);
  onSceneChangeRef.current = props.onSceneChange;

  /** Wrap every write to the scene so the previous snapshot is
   * pushed onto the undo stack first. Use this in place of
   * `props.onSceneChange` for any *user-initiated* write. Reads and
   * updates `sceneRef` so back-to-back same-task commits compose.
   * Undo/redo restore via `onSceneChangeRef` directly so restoring
   * doesn't push onto the stack. */
  const commitScene = (next: BoardScene) => {
    undoStack.push(sceneRef.current);
    sceneRef.current = next;
    onSceneChangeRef.current(next);
  };

  // ---- Stage + Transformer refs ----
  const stageRef = useRef<Konva.Stage | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);

  // Attach Transformer to the selected zone (only zones get resize
  // handles for now; other shapes are draggable but not resizable).
  useEffect(() => {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage || !transformer) return;
    // Finish any in-flight resize before re-targeting: Konva's
    // setNodes() only detaches — without stopTransform() the later
    // window mouseup fires transformend with no target and the node
    // keeps its partial scale mutations forever (nothing resets a
    // scale that isn't a React-controlled prop). stopTransform fires
    // transformend now, so the partial resize commits consistently.
    if (transformer.isTransforming()) transformer.stopTransform();
    if (!selectedId) {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
      return;
    }
    const shape = props.scene.shapes.find((s) => s.id === selectedId);
    // While the pen is armed the freehand drawing-gate exception lets
    // a stroke start on ANY node — keep the Transformer detached so
    // an anchor press can't run a resize and a stroke simultaneously.
    if (shape?.kind === "zone" && armedTool !== "freehand") {
      // Konva nodes get a synthetic id from `name`/`id` props. We
      // attach via `findOne(`#${id}`)` — the ShapeRenderer doesn't
      // currently set an id on its inner node, so look up by name
      // (set below in the editor render path via the wrapping Group).
      const node = stage.findOne(`#${selectedId}`);
      if (node) {
        transformer.nodes([node]);
      } else {
        transformer.nodes([]);
      }
    } else {
      transformer.nodes([]);
    }
    transformer.getLayer()?.batchDraw();
  }, [selectedId, props.scene.shapes, armedTool]);

  // ---- Keyboard shortcuts (gated on the data attribute below) ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // Undo/redo chords — checked before the plain-modifier bail.
      // Read scene state via refs: this listener's effect re-registers
      // in a PASSIVE effect, so a rapid key-repeat can land on a stale
      // closure between React's commit and the listener swap —
      // snapshotting a stale `props.scene` would corrupt the
      // undo/redo chain (duplicate entries, skipped states).
      if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === "z" && !e.shiftKey) {
          e.preventDefault();
          const prev = undoStack.undo(sceneRef.current);
          if (prev) {
            sceneRef.current = prev;
            onSceneChangeRef.current(prev);
          }
          return;
        }
        if ((k === "z" && e.shiftKey) || k === "y") {
          e.preventDefault();
          const next = undoStack.redo(sceneRef.current);
          if (next) {
            sceneRef.current = next;
            onSceneChangeRef.current(next);
          }
          return;
        }
        return; // any other chord belongs to the browser
      }
      if (e.altKey) return;
      const key = e.key.toUpperCase();
      const map: Record<string, EditorToolKind> = {
        V: "select",
        A: "arrow",
        L: "line",
        Z: "zone",
        T: "label",
        F: "freehand",
        P: "player",
        B: "ball",
      };
      // Coach Review owns the letter-key layer while it is mounted: V/B
      // switch source, F opens Focus, and 1-4 select tools. Letting this
      // embedded editor also process the event creates two state changes for
      // one keypress (for example Focus opens and Freehand arms immediately).
      // Keep the editor's Escape/Delete and modifier-key history handling
      // below/above available to Coach Review.
      if (!document.body.dataset.coachReviewSource && key in map) {
        e.preventDefault();
        armTool(map[key]!);
        return;
      }
      if (e.key === "Escape") {
        // Step down one level at a time: cancel an in-flight draw
        // first, then clear selection, then disarm back to Select.
        // Read pending imperatively — the closure copy can be stale
        // in the commit→passive-flush window right after a mouseup.
        const pendingNow =
          useReviewEditorStore.getState().tactical.pendingShape;
        if (pendingNow) {
          cancelPending();
          return;
        }
        if (selectedId) {
          selectShape(null);
          return;
        }
        disarm();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        const current = sceneRef.current;
        const filteredShapes = current.shapes.filter(
          (s) => s.id !== selectedId,
        );
        const filteredTokens = current.tokens.filter(
          (t) => t.id !== selectedId,
        );
        if (
          filteredShapes.length !== current.shapes.length ||
          filteredTokens.length !== current.tokens.length
        ) {
          commitScene({
            ...current,
            shapes: filteredShapes,
            tokens: filteredTokens,
          });
          selectShape(null);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    armTool,
    cancelPending,
    disarm,
    selectedId,
    selectShape,
    props,
    undoStack,
  ]);

  // ---- Stage event helpers ----
  function unitFromStageEvent(
    e: KonvaEventObject<MouseEvent | TouchEvent>,
  ): { x: number; y: number } | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    const p = stage.getPointerPosition();
    if (!p) return null;
    return { x: clamp01(p.x / dims.width), y: clamp01(p.y / dims.height) };
  }

  function handleMouseDown(e: KonvaEventObject<MouseEvent | TouchEvent>) {
    const onStageBackground = e.target === e.target.getStage();
    if (!isToolAllowed(armedTool, "tactical")) return;
    // Drawing starts only on background hits — pointerdown on a shape
    // falls through to Konva's node drag / click-select (direct
    // manipulation). Exception: the freehand pen always draws, even
    // over existing shapes (which aren't draggable while it's armed).
    if (!onStageBackground && armedTool !== "freehand") return;

    const pt = unitFromStageEvent(e);
    if (!pt) return;

    if (armedTool === "select") {
      selectShape(null);
      return;
    }
    if (armedTool === "arrow" || armedTool === "line") {
      const shape: SharedShape = {
        kind: armedTool,
        id: nextId(armedTool),
        x1: pt.x,
        y1: pt.y,
        x2: pt.x,
        y2: pt.y,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "zone") {
      const shape: SharedShape = {
        kind: "zone",
        id: nextId("zone"),
        x: pt.x,
        y: pt.y,
        w: 0.001,
        h: 0.001,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "freehand") {
      const shape: SharedShape = {
        kind: "freehand",
        id: nextId("freehand"),
        points: [pt],
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "label") {
      // Cap at MAX_SHAPES (40) — labels commit directly here rather
      // than through `commitToScene()`, so the cap that guards the
      // drag-shape path has to be repeated. The backend 422s a scene
      // with more than 40 shapes.
      if (props.scene.shapes.length >= MAX_SHAPES) return;
      const trimmed = labelText.trim().slice(0, 80);
      if (!trimmed) return;
      const shape: SharedShape = {
        kind: "label",
        id: nextId("label"),
        x: pt.x,
        y: pt.y,
        text: trimmed,
        color,
        strokeWidth,
      };
      commitScene({
        ...props.scene,
        shapes: [...props.scene.shapes, shape],
      });
      setLabelText("");
      setLabelPopoverOpen(false);
      armTool("select");
      selectShape(shape.id ?? null);
      return;
    }
    if (armedTool === "player" || armedTool === "ball") {
      // Cap at MAX_TOKENS (40) — the backend's
      // `validate_tactical_board_payload` 422s a scene with more.
      // Formation-apply already caps; manual placement must too.
      // Silent no-op at the limit, consistent with degenerate-shape
      // rejection below.
      if (props.scene.tokens.length >= MAX_TOKENS) return;
      const token: BoardToken = {
        kind: armedTool,
        id: nextId(armedTool),
        x: pt.x,
        y: pt.y,
        label: armedTool === "player" ? "" : undefined,
        player_id: "",
        // Player tokens take the selected swatch so a coach can
        // place e.g. blue vs red teams; the ball keeps its own
        // fixed white marker (no color override).
        ...(armedTool === "player" ? { color } : {}),
      };
      commitScene({
        ...props.scene,
        tokens: [...props.scene.tokens, token],
      });
      // Stay armed — placing one token does NOT revert to Select, so
      // the coach can drop a whole lineup in one go. Esc / picking
      // another tool disarms. Auto-select the new token so it can be
      // adjusted (or roster-bound) immediately.
      selectShape(token.id ?? null);
      if (armedTool === "player") setPlayerPopoverOpen(true);
      return;
    }
  }

  function handleMouseMove(e: KonvaEventObject<MouseEvent | TouchEvent>) {
    if (!pending) return;
    const pt = unitFromStageEvent(e);
    if (!pt) return;
    if (pending.kind === "arrow" || pending.kind === "line") {
      updatePending({ ...pending, x2: pt.x, y2: pt.y });
      return;
    }
    if (pending.kind === "zone") {
      const x = Math.min(pending.x, pt.x);
      const y = Math.min(pending.y, pt.y);
      const w = Math.abs(pt.x - pending.x);
      const h = Math.abs(pt.y - pending.y);
      updatePending({ ...pending, x, y, w, h });
      return;
    }
    if (pending.kind === "freehand") {
      const lastPt = pending.points[pending.points.length - 1];
      if (
        lastPt &&
        Math.abs(lastPt.x - pt.x) + Math.abs(lastPt.y - pt.y) < 0.005
      ) {
        return; // skip near-duplicate
      }
      if (pending.points.length >= 200) {
        commitToScene();
        return;
      }
      updatePending({ ...pending, points: [...pending.points, pt] });
      return;
    }
  }

  function commitToScene() {
    const committed = commitPending();
    if (!committed) return;
    // Cap at MAX_SHAPES (40) — the backend 422s a scene with more.
    if (props.scene.shapes.length >= MAX_SHAPES) return;
    const finalShape = clampShape(committed) as BoardShape;
    // Reject degenerate shapes:
    if (
      finalShape.kind === "zone" &&
      (finalShape.w < 0.02 || finalShape.h < 0.02)
    ) {
      return;
    }
    if (finalShape.kind === "freehand" && finalShape.points.length < 2) {
      return;
    }
    if (
      (finalShape.kind === "arrow" || finalShape.kind === "line") &&
      Math.abs(finalShape.x1 - finalShape.x2) < 0.005 &&
      Math.abs(finalShape.y1 - finalShape.y2) < 0.005
    ) {
      return;
    }
    commitScene({
      ...props.scene,
      shapes: [...props.scene.shapes, finalShape],
    });
    // Auto-select the freshly drawn shape so it can be moved /
    // endpoint-adjusted / resized immediately (the tool stays armed
    // for rapid multi-draw; drawing still starts on background hits).
    selectShape(finalShape.id ?? null);
  }

  function handleMouseUp() {
    if (!pending) return;
    commitToScene();
  }

  function replaceShape(shape: BoardShape) {
    const clamped = clampShape(shape);
    const current = sceneRef.current;
    commitScene({
      ...current,
      shapes: current.shapes.map((s) => (s.id === clamped.id ? clamped : s)),
    });
  }

  function replaceToken(token: BoardToken) {
    const clamped = clampToken(token);
    const current = sceneRef.current;
    commitScene({
      ...current,
      tokens: current.tokens.map((t) => (t.id === clamped.id ? clamped : t)),
    });
  }

  /** Commit the inline (double-click) text editor. Labels require
   * text, so an empty commit is a no-op; zone captions clear. */
  function commitTextEdit(id: string, raw: string) {
    setEditingTextId(null);
    const text = raw.trim().slice(0, 80);
    const shape = props.scene.shapes.find((s) => s.id === id);
    if (!shape) return;
    if (shape.kind === "label") {
      if (!text || text === shape.text) return;
      replaceShape({ ...shape, text });
      return;
    }
    if (shape.kind === "zone") {
      if ((text || undefined) === shape.text) return;
      replaceShape({ ...shape, text: text || undefined });
    }
  }

  function handleTransformEnd(e: KonvaEventObject<Event>) {
    const node = e.target as Konva.Node | undefined;
    // A transform aborted after the Transformer was emptied fires
    // transformend with no target node.
    if (!node || typeof node.id !== "function") return;
    const id = node.id();
    const shape = props.scene.shapes.find((s) => s.id === id);
    if (!shape || shape.kind !== "zone") return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    const rect = unitRectFromNodeBox(
      {
        x: node.x(),
        y: node.y(),
        width: node.width() * scaleX,
        height: node.height() * scaleY,
      },
      dims,
    );
    replaceShape({ ...shape, ...rect });
  }

  /** B6: Apply Formation now uses `<ConfirmDialog>` when player
   * tokens exist on the board (the destructive case). With no
   * existing players, apply directly — no confirm needed.
   * `<ConfirmDialog>` is in the JSX below; this just opens it. */
  function handleApplyFormation() {
    const hasPlayers = props.scene.tokens.some((t) => t.kind === "player");
    if (hasPlayers) {
      setConfirm({ kind: "apply-formation", pendingFormationId: formationId });
      return;
    }
    commitApplyFormation(formationId);
  }

  function commitApplyFormation(targetFormationId: string) {
    const newPlayers = playerTokensFromFormation(
      gameFormat,
      targetFormationId,
      () => nextId("player"),
    );
    if (newPlayers === null) {
      // Custom sentinel or unknown — record metadata, preserve tokens.
      commitScene({
        ...props.scene,
        game_format: gameFormat,
        formation: targetFormationId,
      });
      return;
    }
    const ballAndOthers: BoardToken[] = props.scene.tokens.filter(
      (t) => t.kind !== "player",
    );
    const safeTokens = [...ballAndOthers, ...newPlayers].slice(0, 40);
    commitScene({
      ...props.scene,
      game_format: gameFormat,
      formation: targetFormationId,
      tokens: safeTokens,
    });
    selectShape(null);
  }

  /** Update a single token's `label` + `player_id` after the
   * coach picks from the roster. */
  function bindPlayerToToken(tokenId: string, player: Player) {
    const labelFromPlayer = player.jersey_number
      ? `#${player.jersey_number} ${player.display_name}`
      : player.display_name;
    // Cap at the schema's MAX_TOKEN_LABEL_LENGTH (24) — see
    // `lib/canvas/board-schema.ts:48`.
    const label = labelFromPlayer.slice(0, 24);
    const current = sceneRef.current;
    commitScene({
      ...current,
      tokens: current.tokens.map((t) =>
        t.id === tokenId ? { ...t, player_id: player.id, label } : t,
      ),
    });
  }

  function clearPlayerBinding(tokenId: string) {
    const current = sceneRef.current;
    commitScene({
      ...current,
      tokens: current.tokens.map((token) =>
        token.id === tokenId ? { ...token, player_id: "" } : token,
      ),
    });
  }

  const presets = formationsForGameFormat(gameFormat);
  const formationControls = (
    <div
      className="flex items-center gap-2"
      data-testid="board-formation-controls"
    >
      <span className="shrink-0 text-xs font-display text-ink-muted uppercase tracking-wider">
        Formation
      </span>
      {!props.compactFormationControls ? (
        <TkticSelect
          value={gameFormat}
          onChange={(v) => {
            const g = v as GameFormat;
            setGameFormat(g);
            const first = formationsForGameFormat(g)[0];
            if (first) setFormationId(first.id);
          }}
          options={GAME_FORMATS.map((g) => ({ value: g, label: g }))}
          className="w-24"
          nativeSelectProps={{ "data-canvas-game-format": "" }}
        />
      ) : null}
      <TkticSelect
        value={formationId}
        onChange={setFormationId}
        options={presets.map((p) => ({ value: p.id, label: p.label }))}
        className="w-36"
        nativeSelectProps={{ "data-canvas-formation": "" }}
      />
      <button
        type="button"
        onClick={handleApplyFormation}
        data-canvas-apply-formation
        className="shrink-0 rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-accent-ink transition-colors hover:bg-accent-hover"
      >
        {props.compactFormationControls ? "Apply formation" : "Apply Formation"}
      </button>
      {props.toolRailSlot ? (
        <button
          type="button"
          className="board-flip"
          onClick={() => commitScene(flipBoard(sceneRef.current))}
        >
          <FlipHorizontal2 size={18} aria-hidden />
          Flip pitch
        </button>
      ) : null}
    </div>
  );

  return (
    <div
      className="flex flex-col lg:flex-row gap-4 w-full"
      data-coach-review-source="tactical_board"
    >
      {/* The board stage IS the 16:9 green field — no letterbox
       * frame any more. `tactical-board-slot` + `-stage-container`
       * are now the same 16:9 box (kept as nested divs so both
       * test ids resolve). */}
      <div
        className="flex-shrink-0 touch-none overflow-hidden rounded"
        style={{ width, height }}
        data-testid="tactical-board-slot"
      >
        <div
          className="relative touch-none overflow-hidden"
          style={{ width, height, cursor: cursors.baseCursor }}
          data-testid="tactical-board-stage-container"
        >
          <Stage
            ref={stageRef}
            width={width}
            height={height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            onMouseOver={cursors.onMouseOver}
            onMouseOut={cursors.onMouseOut}
          >
            <Layer listening={false}>
              <PitchBackground dims={dims} appearance={props.appearance} />
            </Layer>
            {/* M7.5 C1: hide the drawing layer when Canvas is "Off".
             * Pitch stays visible; only the shapes / tokens fade. */}
            <Layer visible={canvasVisible}>
              {props.scene.shapes.map((shape, i) => (
                <ShapeRenderer
                  key={shape.id ?? `shape-${i}`}
                  shape={shape}
                  dims={dims}
                  selected={shape.id === selectedId}
                  onSelect={() => {
                    // The pen draws over shapes; a click mustn't
                    // hijack selection mid-stroke.
                    if (armedTool === "freehand") return;
                    selectShape(shape.id ?? null);
                    setPlayerPopoverOpen(false);
                  }}
                  draggable={shapesDraggable}
                  onDragEnd={(s) => replaceShape(s as BoardShape)}
                  onTextEdit={
                    shape.kind === "label" || shape.kind === "zone"
                      ? () => setEditingTextId(shape.id ?? null)
                      : undefined
                  }
                />
              ))}
              {props.scene.tokens.map((token, i) => (
                <TokenRenderer
                  key={token.id ?? `token-${i}`}
                  token={token}
                  dims={dims}
                  selected={token.id === selectedId}
                  onSelect={() => {
                    if (armedTool === "freehand") return;
                    selectShape(token.id ?? null);
                    setPlayerPopoverOpen(token.kind === "player");
                  }}
                  draggable={shapesDraggable}
                  onDragEnd={replaceToken}
                />
              ))}
              {pending ? <ShapeRenderer shape={pending} dims={dims} /> : null}
              <Transformer
                ref={transformerRef}
                rotateEnabled={false}
                enabledAnchors={[
                  "top-left",
                  "top-center",
                  "top-right",
                  "middle-right",
                  "middle-left",
                  "bottom-left",
                  "bottom-center",
                  "bottom-right",
                ]}
                boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 8 || newBox.height < 8) return oldBox;
                  return newBox;
                }}
                onTransformEnd={handleTransformEnd}
              />
              {/* Endpoint anchors for the selected arrow/line — last
               * children of the layer so they sit on top of the hit
               * graph and beat the underlying draggable line. */}
              {(() => {
                // Hidden while the pen is armed — the freehand gate
                // exception would otherwise start a stroke AND a handle
                // drag from one pointerdown on an anchor.
                if (armedTool === "freehand") return null;
                const sel = props.scene.shapes.find((s) => s.id === selectedId);
                if (!sel || (sel.kind !== "arrow" && sel.kind !== "line")) {
                  return null;
                }
                return (
                  <EndpointHandles
                    shape={sel}
                    dims={dims}
                    onCommit={(s) => replaceShape(s as BoardShape)}
                  />
                );
              })()}
            </Layer>
          </Stage>
          {/* Double-click inline text editor for labels / zone
           * captions — an HTML input overlaid on the stage. */}
          {(() => {
            if (!editingTextId) return null;
            const shape = props.scene.shapes.find(
              (s) => s.id === editingTextId,
            );
            if (!shape || (shape.kind !== "label" && shape.kind !== "zone")) {
              return null;
            }
            const p = pointToPx({ x: shape.x, y: shape.y }, dims);
            return (
              <InlineTextEditor
                // Clamp inside the overflow-hidden stage box so an
                // edge-shape's editor isn't clipped out of view
                // (input is w-44 = 176px + padding).
                x={Math.max(0, Math.min(p.x, dims.width - 184))}
                y={Math.max(0, Math.min(p.y, dims.height - 40))}
                initialValue={
                  shape.kind === "label" ? shape.text : (shape.text ?? "")
                }
                maxLength={80}
                placeholder={
                  shape.kind === "zone" ? "Zone caption" : "Label text"
                }
                onCommit={(text) => commitTextEdit(editingTextId, text)}
                onCancel={() => setEditingTextId(null)}
                data-testid="board-inline-text-editor"
              />
            );
          })()}
        </div>
      </div>

      {props.hideToolbar
        ? null
        : (() => {
            // The rail body — tools + optional formation controls +
            // label / token editors + canvas controls + clear board.
            // The v2 `toolRailSlot` relocates this block into the
            // Coach Review right rail so the pitch surface stays
            // chrome-free; without a slot it renders in the canvas's
            // own `w-[300px]` column as before. When
            // `formationControlsSlot` is also set the formation
            // controls are routed there instead and excluded here
            // (see the `formationControlsSlot ? null : …` below).
            const railContent = (
              <>
                <ToolGrid
                  horizontal={Boolean(props.toolRailSlot)}
                  tools={TACTICAL_TOOLS}
                  value={armedTool}
                  onChange={(tool) => {
                    armTool(tool);
                    setLabelPopoverOpen(tool === "label");
                    setPlayerPopoverOpen(
                      tool === "player" && Boolean(selectedPlayerToken),
                    );
                  }}
                  toolPopovers={{
                    ...(armedTool === "label" && labelPopoverOpen
                      ? {
                          label: (
                            <label className="canvas-label-settings">
                              <span>Label</span>
                              <input
                                autoFocus
                                type="text"
                                data-testid="board-label-text-input"
                                value={labelText}
                                onChange={(event) =>
                                  setLabelText(event.target.value)
                                }
                                maxLength={80}
                                aria-label="Label"
                              />
                              <small>Enter text, then tap the canvas.</small>
                            </label>
                          ),
                        }
                      : {}),
                    ...(selectedPlayerToken && playerPopoverOpen
                      ? {
                          player: (
                            <div
                              className="canvas-player-settings"
                              data-testid="board-player-token-editor"
                            >
                              <span>
                                Player token
                                {selectedPlayerToken.player_id
                                  ? " · linked"
                                  : ""}
                              </span>
                              <label>
                                <span>Token label</span>
                                <input
                                  type="text"
                                  value={selectedPlayerToken.label ?? ""}
                                  onChange={(event) => {
                                    const nextLabel = event.target.value.slice(
                                      0,
                                      24,
                                    );
                                    const current = sceneRef.current;
                                    commitScene({
                                      ...current,
                                      tokens: current.tokens.map((token) =>
                                        token.id === selectedPlayerToken.id
                                          ? { ...token, label: nextLabel }
                                          : token,
                                      ),
                                    });
                                  }}
                                  maxLength={24}
                                  placeholder="Token label (#10 Drew)"
                                  data-testid="board-player-token-label-input"
                                  aria-label="Token label"
                                />
                              </label>
                              <label>
                                <span>Player</span>
                                <TkticSelect
                                  value={selectedPlayerToken.player_id ?? ""}
                                  onChange={(playerId) => {
                                    const player = props.players?.find(
                                      (candidate) => candidate.id === playerId,
                                    );
                                    if (player) {
                                      bindPlayerToToken(
                                        selectedPlayerToken.id ?? "",
                                        player,
                                      );
                                    } else {
                                      clearPlayerBinding(
                                        selectedPlayerToken.id ?? "",
                                      );
                                    }
                                  }}
                                  options={[
                                    { value: "", label: "Pick player" },
                                    ...(props.players ?? []).map((player) => ({
                                      value: player.id,
                                      label: player.jersey_number
                                        ? `#${player.jersey_number} ${player.display_name}`
                                        : player.display_name,
                                    })),
                                  ]}
                                  ariaLabel="Pick player for token"
                                  data-testid="board-player-token-player"
                                  size="sm"
                                />
                              </label>
                            </div>
                          ),
                        }
                      : {}),
                  }}
                />
                {props.toolRailSlot ? (
                  <DrawingStyle
                    onOpen={() => {
                      setLabelPopoverOpen(false);
                      setPlayerPopoverOpen(false);
                    }}
                  >
                    <ColorPalette
                      approved={Boolean(props.toolRailSlot)}
                      value={color}
                      onChange={setColor}
                    />
                    <StrokeWidthSlider
                      value={strokeWidth}
                      onChange={setStrokeWidth}
                      label="Stroke width"
                    />
                  </DrawingStyle>
                ) : (
                  <>
                    <ColorPalette
                      approved={Boolean(props.toolRailSlot)}
                      value={color}
                      onChange={setColor}
                    />
                    <StrokeWidthSlider
                      value={strokeWidth}
                      onChange={setStrokeWidth}
                    />
                  </>
                )}
                {props.formationControlsSlot ? null : formationControls}
                {/* M7.5 C1 / C2 / C3: Canvas-rail controls. */}
                <CanvasRailControls
                  compact={Boolean(props.toolRailSlot)}
                  visible={canvasVisible}
                  onToggleVisible={() => setCanvasVisible((v) => !v)}
                  undoDepth={undoStack.depth}
                  onUndo={() => {
                    const prev = undoStack.undo(sceneRef.current);
                    if (prev) {
                      sceneRef.current = prev;
                      onSceneChangeRef.current(prev);
                    }
                  }}
                  redoDepth={undoStack.redoDepth}
                  onRedo={() => {
                    const next = undoStack.redo(sceneRef.current);
                    if (next) {
                      sceneRef.current = next;
                      onSceneChangeRef.current(next);
                    }
                  }}
                  hasSelection={selectedId != null}
                  onDeleteSelected={() => {
                    if (!selectedId) return;
                    const current = sceneRef.current;
                    const nextShapes = current.shapes.filter(
                      (s) => s.id !== selectedId,
                    );
                    const nextTokens = current.tokens.filter(
                      (t) => t.id !== selectedId,
                    );
                    commitScene({
                      ...current,
                      shapes: nextShapes,
                      tokens: nextTokens,
                    });
                    selectShape(null);
                  }}
                  testidPrefix="board-rail"
                />

                <div className="flex flex-col gap-2 mt-auto">
                  {/* M7.5 B4: styled confirm replaces `window.confirm`. */}
                  <button
                    type="button"
                    onClick={() => setConfirm({ kind: "clear-board" })}
                    data-canvas-clear-board
                    className="border border-destructive/40 text-destructive hover:bg-destructive/10 rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
                  >
                    {props.toolRailSlot ? (
                      <>
                        <Trash2 size={18} aria-hidden />
                        <span className="canvas-compact-label">
                          Clear board
                        </span>
                      </>
                    ) : (
                      "Clear board"
                    )}
                  </button>
                </div>
                {props.toolbarChildren ? (
                  <div className="flex flex-col gap-4 pt-4 border-t border-rule/40">
                    {props.toolbarChildren}
                  </div>
                ) : null}
              </>
            );
            // With a `toolRailSlot`, hand the whole rail to the
            // caller and render NO own column. Otherwise render the
            // canvas's own `w-[300px]` rail column.
            return props.toolRailSlot ? (
              props.toolRailSlot(railContent)
            ) : (
              <div
                className="flex max-h-[55dvh] w-full flex-col gap-4 overflow-y-auto rounded-panel border border-rule bg-surface p-3 lg:max-h-[var(--tb-stage-h)] lg:w-[300px] lg:flex-shrink-0 lg:border-0 lg:bg-transparent lg:p-0"
                style={{ "--tb-stage-h": `${height}px` } as React.CSSProperties}
                data-testid="canvas-toolbar-rail"
              >
                {railContent}
              </div>
            );
          })()}

      {!props.hideToolbar && props.formationControlsSlot
        ? props.formationControlsSlot(formationControls)
        : null}

      {/* M7.5 B4 / B6: styled confirms for destructive actions. */}
      {confirm?.kind === "clear-board" ? (
        <ConfirmDialog
          title="Clear the board?"
          message="This removes every token and shape. This can't be undone."
          confirmLabel="Clear board"
          danger
          onConfirm={() => {
            commitScene(emptyScene());
            selectShape(null);
            setConfirm(null);
          }}
          onClose={() => setConfirm(null)}
          data-testid="board-clear-confirm"
        />
      ) : null}
      {confirm?.kind === "apply-formation" ? (
        <ConfirmDialog
          title="Apply this formation?"
          message="Existing player tokens will be replaced. Ball and shapes stay."
          confirmLabel="Apply formation"
          danger
          onConfirm={() => {
            const target = confirm.pendingFormationId;
            setConfirm(null);
            commitApplyFormation(target);
          }}
          onClose={() => setConfirm(null)}
          data-testid="board-apply-formation-confirm"
        />
      ) : null}
    </div>
  );
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
